<template>
  <div class="z-100 flex h-screen w-full flex-col bg-gray-900">
    <!-- Loading state -->

    <!-- Editor UI -->
    <div class="flex h-full flex-col">
      <!-- Top toolbar -->

      <!-- Main content area -->
      <div class="relative flex flex-grow overflow-hidden">
        <div ref="canvasContainer" class="relative flex-grow overflow-hidden bg-gray-700"></div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useRoute } from 'vue-router';

import { useAssetStore } from '@/stores/api/asset';
import { useCruxModuleStore } from '@/stores/api/cruxmodules';

import { cleanupScene, hasSceneLoaded, initScene, renderer } from '@/threejs/core';

import { useHistoryTracker } from '@/composables/useHistoryTracker';
import { useKeyboardShortcuts } from '@/composables/useKeyboardShortcuts';
import { useWebsockets } from '@/composables/useWebsockets';

const route = useRoute();
const assetStore = useAssetStore();
const cruxModuleStore = useCruxModuleStore();
const sceneId = route.params.id;
const canvasContainer = ref(null);
const loading = ref(true);
const error = ref(null);
const cleanupSelectionEvents = ref(null);

const { connectWebsockets, cleanup: cleanupWebsockets } = useWebsockets();

const cameraPositionRef = ref({ x: 0, y: 0, z: 0 });

useKeyboardShortcuts();
useHistoryTracker();
onMounted(async () => {
  hasSceneLoaded.value = false;
  loadData();
});

// Watch for changes to the canvasContainer
watch(canvasContainer, (newContainer) => {
  if (newContainer && !hasSceneLoaded.value) {
    initThreeJs();
  }
});

onBeforeUnmount(() => {
  cleanupWebsockets();
  cleanupThreeJs();
});

const loadData = async () => {
  try {
    if (sceneId) {
      const asset = await assetStore.fetch(sceneId);
      assetStore.currentAsset = asset;
    }

    if (assetStore.currentAsset) {
      await cruxModuleStore.fetchforAsset(assetStore.currentAsset.code);
    }

    await nextTick();

    connectWebsockets();

    loading.value = false;
  } catch (err) {
    console.error('Error initializing editor:', err);
    error.value = 'Failed to load the 3D editor. Please try again.';
    loading.value = false;
  }
};

const initThreeJs = async () => {
  if (!canvasContainer.value) {
    console.error('Canvas container not found');
    return;
  }

  await nextTick();

  // Check if container has dimensions
  const { clientWidth, clientHeight } = canvasContainer.value;
  if (clientWidth === 0 || clientHeight === 0) {
    console.error('Canvas container has zero dimensions', {
      clientWidth,
      clientHeight,
    });

    return;
  }

  // Initialize the Three.js scene with our container element
  const { scene, camera, cameraControls } = initScene(canvasContainer.value);

  cameraControls.addEventListener('update', () => {
    cameraPositionRef.value.x = cameraControls.camera.position.x.toFixed(2);
    cameraPositionRef.value.y = cameraControls.camera.position.y.toFixed(2);
    cameraPositionRef.value.z = cameraControls.camera.position.z.toFixed(2);
  });

  hasSceneLoaded.value = true;
};

const cleanupThreeJs = () => {
  // Clean up Three.js resources
  cleanupScene();

  // Clean up selection event listeners
  if (cleanupSelectionEvents.value) {
    cleanupSelectionEvents.value();
  }

  // Remove the renderer from the DOM
  if (canvasContainer.value && renderer.domElement.parentNode) {
    canvasContainer.value.removeChild(renderer.domElement);
  }
};
</script>
